import React from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { change, Field, formValueSelector } from "redux-form";
import {
  AutoCompleteField,
  Select,
  FormInput
} from "MODULES/shared/components/Forms";
import {
  CompanyStatsTooltip,
  DashedSeparator
} from "MODULES/shared/components";
import { APPROVAL_STATUSES, WEBSITE } from "MODULES/companies/constants";
import { searchContacts } from "MODULES/parts/actions/actions";
import * as actions from "MODULES/PurchaseOrders/actions";
import FormRow from "MODULES/shared/components/Forms/FormRow";
import { Col } from "react-bootstrap";
import {
  BUYER_CREDIT_PCT,
  COMPANY_ID,
  CONTACT_ID,
  FOB
} from "APP_ROOT/constants";
import LocationRadioField from "MODULES/Locations/components/BuyingStoreRadioField";
import {
  contactSelectList,
  selectPrimaryContact
} from "MODULES/companies/helpers";
import {
  BUYER_ID,
  CO_BUYER_ID,
  THIRD_BUYER_ID,
  USER
} from "MODULES/users/constants";
import { get } from "lodash";
import { properCase } from "LIB/formatters/text";
import { PORTAL_PURCHASE } from "MODULES/PurchaseOrders/constants";
import CoBuyersFields from "MODULES/shared/components/CoBuyersFields/CoBuyersFields";
import PaymentTerms from "MODULES/shared/components/PaymentTerms/PaymentTerms";

export class DetailFields extends React.Component {
  static propTypes = {
    buyerCreditPct: PropTypes.string,
    contact: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    companyType: PropTypes.string,
    currentUser: PropTypes.shape({}),
    getField: PropTypes.func,
    managedRepairOrder: PropTypes.instanceOf(Object),
    searchBuyers: PropTypes.func.isRequired,
    searchCompanies: PropTypes.func.isRequired,
    notifyForm: PropTypes.func,
    setPaymentTerms: PropTypes.func,
    selectedBuyer: PropTypes.instanceOf(Object),
    selectedCoBuyer: PropTypes.instanceOf(Object),
    selectedThirdBuyer: PropTypes.instanceOf(Object),
    setField: PropTypes.func,
    setPrimaryContact: PropTypes.bool,
    setSelectedCompany: PropTypes.func
  };

  static defaultProps = {
    companyType: "Vendor",
    setPrimaryContact: true
  };

  state = {
    contacts: []
  };

  componentWillMount() {
    if (
      this.props.managedRepairOrder &&
      this.props.managedRepairOrder.company
    ) {
      this.fetchContacts(this.props.managedRepairOrder.company.id);
      const { selectedCoBuyer, selectedThirdBuyer } = this.props;
      this.setState({
        selectedCoBuyer,
        selectedThirdBuyer
      });
    }
  }

  fetchContacts = value => {
    if (!value) {
      return null;
    }

    return searchContacts(value).then((vals = []) => {
      this.setState({ contacts: vals });

      if (this.props.setPrimaryContact) {
        selectPrimaryContact(vals, this.props.setField(CONTACT_ID));
      }
    });
  };

  handleCompanyChange = result => {
    if (this.props.setSelectedCompany) this.props.setSelectedCompany(result);

    if (this.props.notifyForm) this.props.notifyForm(result);

    if (result) {
      const address = result.primary_physical_address;
      const formattedAddress = address
        ? [address.city, address.state, address.country].join("-")
        : "";
      this.props.setField(FOB)(formattedAddress);
      if (this.props.setPaymentTerms)
        this.props.setPaymentTerms(result.vendor_credit_terms);
      this.fetchContacts(result.id);
    } else {
      this.setState({ contacts: [] });
    }
  };

  render() {
    const {
      contacts,
      selectedCompany,
      selectedCoBuyer,
      selectedThirdBuyer
    } = this.state;
    const {
      contact,
      companyType,
      currentUser,
      getField,
      managedRepairOrder,
      searchCompanies,
      selectedBuyer,
      searchBuyers,
      setField,
      buyerCreditPct
    } = this.props;
    const buyerCreditPctOptions = [
      { text: "75%", value: "75.0" },
      { text: "50%", value: "50.0" },
      { text: "25%", value: "25.0" }
    ];
    const options = contactSelectList(contacts, true);
    const company =
      selectedCompany || (managedRepairOrder && managedRepairOrder.company);
    const toggleBuyer = buyer => this.setState(buyer);

    return (
      <div>
        <FormRow>
          {currentUser.tenant.locations_count !== 1 && (
            <Col md={5}>
              <LocationRadioField label="Managing Store" />
            </Col>
          )}
          <Col md={4}>
            <Field
              name={BUYER_ID}
              label="Buyer"
              placeholder="Search by name..."
              component={AutoCompleteField}
              search={searchBuyers}
              selectedItem={selectedBuyer}
              formatItem={result => result && result.full_name}
              required
            />
          </Col>
          <Col md={3}>
            <Field
              name={BUYER_CREDIT_PCT}
              className="text-right"
              component={Select}
              onChange={(_ev, val) => {
                if (["25.0", "50.0", null].includes(val)) {
                  setField(THIRD_BUYER_ID)(null);
                  toggleBuyer({ selectedThirdBuyer: null });
                }
              }}
              options={buyerCreditPctOptions}
              label="Buyer Credit"
              required
            />
          </Col>
        </FormRow>
        <FormRow>
          <CoBuyersFields
            buyerCreditPct={buyerCreditPct}
            searchBuyers={searchBuyers}
            selectedCoBuyer={selectedCoBuyer}
            selectedThirdBuyer={selectedThirdBuyer}
            toggleBuyer={toggleBuyer}
            numberOfBuyers={
              [getField(CO_BUYER_ID), getField(THIRD_BUYER_ID)].filter(s => s)
                .length + 1
            }
          />
        </FormRow>
        <DashedSeparator />
        <FormRow>
          <Col md={6}>
            <Field
              name={COMPANY_ID}
              label={companyType}
              labelChildren={
                company && (
                  <React.Fragment>
                    <CompanyStatsTooltip company={company} />
                  </React.Fragment>
                )
              }
              placeholder="Search by typing a company's name or code"
              component={AutoCompleteField}
              search={searchCompanies}
              formatItem={result =>
                `${result.name} (${APPROVAL_STATUSES[result.vendor_status]
                  .label})`}
              onItemSelected={i => i && this.handleCompanyChange(i)}
              selectedItem={company}
              required
            />
          </Col>
          <Col md={3}>
            <Field
              name={CONTACT_ID}
              component={Select}
              options={options}
              label={`${companyType} Contact`}
              required
            />
          </Col>
          <Col md={3}>
            <PaymentTerms />
          </Col>
        </FormRow>
        {contact === PORTAL_PURCHASE && (
          <FormRow>
            <Col md={12}>
              <div className="form-group">
                <Field
                  component={FormInput}
                  disabled={get(company, WEBSITE)}
                  name="company.website"
                  label={properCase(WEBSITE)}
                  required
                />
              </div>
            </Col>
          </FormRow>
        )}
        {contact === "Add" && (
          <FormRow>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name="contact.name"
                  component={FormInput}
                  label="Contact Name"
                  required
                />
              </div>
            </Col>
            <Col md={6}>
              <div className="form-group">
                <Field
                  name="contact.email"
                  component={FormInput}
                  label="Contact Email"
                  required
                />
              </div>
            </Col>
          </FormRow>
        )}
      </div>
    );
  }
}

const selector = (form, ...other) => formValueSelector(form)(...other);

export default connect(
  (state, ownProps) => ({
    currentUser: state.session.get(USER).toJS(),
    buyerCreditPct: selector(ownProps.formName, state, BUYER_CREDIT_PCT),
    contact: selector(ownProps.formName, state, CONTACT_ID),
    getField: field => selector(ownProps.formName, state, field)
  }),
  (dispatch, ownProps) => ({
    searchCompanies: filter => dispatch(actions.searchCompanies(filter)),
    searchBuyers: filter => dispatch(actions.searchBuyers(filter)),
    setField: field => val => dispatch(change(ownProps.formName, field, val))
  })
)(DetailFields);



// WEBPACK FOOTER //
// ./app/modules/ManagedRepairOrders/shared/DetailFields.jsx